import { reduce } from "lodash";

import fbLogo from "@assets/fb-logo.svg";
import googleAdsLogo from "@assets/ga-logo.svg";
import klaviyoLogo from "@assets/klaviyo-icon.png";
import microsoftLogo from "@assets/microsoft-icon.png";
import pinterestLogo from "@assets/pinterest-logo-icon.svg";
import snapchatLogo from "@assets/snapchat-logo.svg";
import tiktokLogo from "@assets/tiktok-icon.svg";
import { type NorthbeamUtmPlatform } from "@features/utm-guide/configs";
import { useFeatureFlags } from "@hooks/http";
import { ConnectionType } from "@north-beam/nb-client-side";

import { type StepMetadata } from "..";

import { Platform, UtmChangeImpact, UtmParam } from "./constants";
import { type PlatformMetadata, type UtmPlatformParam } from "./types";

type PlatformsMetadataMapping = Record<Platform, PlatformMetadata>;
type RawPlatformsMetadataMapping = Record<
  keyof PlatformsMetadataMapping,
  PlatformsMetadataMapping[keyof PlatformsMetadataMapping] & {
    enableIntegrationFeatureFlagName?: string;
  }
>;

export const NORTHBEAM_PLATFORM_CONFIGS: RawPlatformsMetadataMapping = {
  [Platform.AMAZON_ADS]: {
    name: Platform.AMAZON_ADS,
    title: "Amazon Ads",
    relatedPlatforms: [Platform.AMAZON_SELLER],
    iconName: "amazon",
    connectionType: ConnectionType.integration_amazon,
  },

  [Platform.AMAZON_SELLER]: {
    name: Platform.AMAZON_SELLER,
    title: "Amazon Seller",
    iconName: "amazon",
    connectionType: ConnectionType.integration_amazon_seller,
  },
  [Platform.UNKNOWN]: {
    name: Platform.UNKNOWN,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
  },
  [Platform.ATTENTIVE]: {
    title: "Attentive Mobile",
    name: Platform.ATTENTIVE,
    connectionType: ConnectionType.integration_attentive,
    enableIntegrationFeatureFlagName: "enableAttentiveConnection",
  },

  [Platform.BLISS_POINT_MEDIA]: {
    name: Platform.BLISS_POINT_MEDIA,
    title: "Bliss Point Media",
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmCompletionText: {
      title:
        "Confirm you’ve submitted a ticket with your Bliss Point Media AWS Keys and S3 Bucket Name.",
      label:
        "I’ve submitted a ticket with your Bliss Point Media AWS Keys and S3 Bucket Name",
    },
  },

  [Platform.TRADE_DESK]: {
    name: Platform.TRADE_DESK,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    skipUtmConfirmationStep: true,
    connectionType: ConnectionType.integration_trade_desk,
    enableIntegrationFeatureFlagName: "enableTradeDeskConnection",
  },

  [Platform.AD_ROLL]: {
    name: Platform.AD_ROLL,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "adroll" },
      { key: UtmParam.MEDIUM, value: "paid" },
      { key: "nb_platform", value: "adroll" },
    ],
  },
  [Platform.AVANT_LINK]: {
    name: Platform.AVANT_LINK,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: "nb_platform", value: "avantlink" },
      { key: UtmParam.SOURCE, value: "[PUBLISHER_ID]" },
      { key: UtmParam.MEDIUM, value: "avantlink" },
      { key: UtmParam.CAMPAIGN, value: "[AD_NAME]" },
    ],
  },

  [Platform.CNN5_THINGS]: {
    name: Platform.CNN5_THINGS,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: "nb_platform", value: "CNN" },
      { key: UtmParam.SOURCE, value: "CNN" },
      { key: UtmParam.MEDIUM, value: "partner-newsletter" },
      {
        key: UtmParam.CAMPAIGN,
        value: () =>
          `CNN5Things${new Date()
            .toLocaleString("default", { month: "short", year: "numeric" })
            .replaceAll(" ", "")}`,
      },
    ],
  },
  [Platform.CRITEO]: {
    name: Platform.CRITEO,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "criteo" },
      { key: UtmParam.MEDIUM, value: "paid" },
      { key: "nb_platform", value: "criteo" },
    ],
  },

  [Platform.GRIN]: {
    name: Platform.GRIN,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      { key: UtmParam.CAMPAIGN, value: "[instagram_username]" },
      { key: UtmParam.SOURCE, value: "grin" },
      { key: UtmParam.MEDIUM, value: "influencer" },
      { key: "nb_platform", value: "grin" },
    ],
  },
  [Platform.IMPACT_RADIUS]: {
    name: Platform.IMPACT_RADIUS,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "{irmpname}" },
      { key: UtmParam.MEDIUM, value: "impact" },
      { key: UtmParam.CAMPAIGN, value: "{ircid}" },
      { key: UtmParam.CONTENT, value: "{iradname}" },
      { key: "nb_platform", value: "impact" },
    ],
  },
  [Platform.KLAVIYO]: {
    name: Platform.KLAVIYO,
    logoUrl: klaviyoLogo,
    connectionCardTitle:
      "1. Submit your public and private Klaviyo keys to establish Northbeam’s Klaviyo integration",
    connectionType: ConnectionType.integration_klaviyo,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      {
        key: "nb_klid",
        value: "[Klaviyo Profile Id]",
      },
    ],
  },
  [Platform.LIVE_INTENT]: {
    name: Platform.LIVE_INTENT,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: "nb_platform", value: "liveintent" },

      { key: UtmParam.SOURCE, value: "liveintent" },
    ],
  },

  [Platform.OUTBRAIN]: {
    name: Platform.OUTBRAIN,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "outbrain" },
      { key: UtmParam.MEDIUM, value: "discovery" },
      { key: UtmParam.CAMPAIGN, value: "{{campaign_name}}" },
      { key: UtmParam.CONTENT, value: "{{ad_title}}" },
      { key: UtmParam.TERM, value: "{{publisher_name}}_{{section_name}}" },
      { key: "nb_platform", value: "outbrain" },
    ],
  },
  [Platform.PARTNERIZE]: {
    name: Platform.PARTNERIZE,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      { key: UtmParam.SOURCE, value: "partnerize" },
      { key: UtmParam.MEDIUM, value: "affiliate" },
      { key: UtmParam.CAMPAIGN, value: "{subid}" },
      { key: UtmParam.CONTENT, value: "{creative}" },
      { key: "nb_platform", value: "partnerize" },
    ],
  },

  [Platform.POSTSCRIPT]: {
    name: Platform.POSTSCRIPT,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      { key: UtmParam.SOURCE, value: "DYNAMIC - CAMPAIGN NAME" },
      { key: UtmParam.MEDIUM, value: "sms" },
    ],
  },

  [Platform.RETENTION_SCIENCE]: {
    name: Platform.RETENTION_SCIENCE,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "ReSci-{{ rs_stage_name }}" },
      { key: UtmParam.MEDIUM, value: "email" },
      { key: UtmParam.CAMPAIGN, value: "{{ rs_template_name }}" },
      { key: "nb_platform", value: "resci" },
      { key: "nb_sn", value: "{{ rs_stage_name }}" },
      { key: "nb_tn", value: "{{ rs_template_name }}" },
      { key: "nb_oid", value: "{{ rs_offerid }}" },
    ],
  },
  [Platform.REVCONTENT]: {
    name: Platform.REVCONTENT,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "revcontent" },
      { key: UtmParam.MEDIUM, value: "native" },
      { key: UtmParam.CAMPAIGN, value: "{boost_id}" },
      { key: UtmParam.TERM, value: "{adv_targets}" },
      { key: UtmParam.CONTENT, value: "{content_headline}" },
      { key: "nb_platform", value: "revcontent" },
    ],
  },

  [Platform.SHARE_A_SALE]: {
    name: Platform.SHARE_A_SALE,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      { key: UtmParam.SOURCE, value: "shareasale" },
      { key: UtmParam.MEDIUM, value: "affiliate" },
      { key: "nb_platform", value: "shareasale" },
    ],
  },
  [Platform.STACK_ADAPT]: {
    name: Platform.STACK_ADAPT,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: UtmParam.SOURCE, value: "stackadapt" },
      { key: UtmParam.MEDIUM, value: "display" },
      { key: UtmParam.CAMPAIGN, value: "{SA_CAMPAIGN_NAME}" },
      { key: UtmParam.CONTENT, value: "{SA_CREATIVE_NAME}" },
      { key: UtmParam.TERM, value: "{SA_APP_NAME}_{SA_BUNDLE_ID}" },
      { key: "nb_platform", value: "stackadapt" },
    ],
  },
  [Platform.TABOOLA]: {
    name: Platform.TABOOLA,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      { key: UtmParam.SOURCE, value: "taboola" },
      { key: UtmParam.MEDIUM, value: "referral" },
      { key: UtmParam.CAMPAIGN, value: "{campaign_name}" },
      { key: UtmParam.TERM, value: "{site}" },
      { key: UtmParam.CONTENT, value: "{title}" },
      { key: "nb_platform", value: "taboola" },
    ],
  },

  [Platform.YAHOO_DSP]: {
    name: Platform.YAHOO_DSP,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      { key: "nb_platform", value: "ydsp" },

      { key: UtmParam.SOURCE, value: "yahoo-dsp" },
      { key: UtmParam.MEDIUM, value: "paid" },
      { key: UtmParam.CAMPAIGN, value: "{ORDERID}" },
    ],
  },

  [Platform.OTHER]: {
    name: Platform.OTHER,
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
    utmParams: [
      { key: UtmParam.SOURCE, value: "{Campaign Level}" },
      { key: UtmParam.MEDIUM, value: "{Campaign Level}" },
      { key: UtmParam.CAMPAIGN, value: "{Campaign Level (line item)}" },
      { key: UtmParam.TERM, value: "{Adset Level (line item)}" },
      { key: UtmParam.CONTENT, value: "{Ad Level (line item)}" },
    ],
  },

  [Platform.GOOGLE]: {
    name: Platform.GOOGLE,
    title: "Google Ads",
    logoUrl: googleAdsLogo,
    connectionType: ConnectionType.integration_google_adwords,
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    utmParams: [
      {
        key: "nbt",
        value:
          "nb%3Aadwords%3A{network}%3A{campaignid}%3A{adgroupid}%3A{creative}",
      },
    ],
  },
  [Platform.PINTEREST]: {
    name: Platform.PINTEREST,
    connectionType: ConnectionType.integration_pinterest,
    utmChangeImpact: UtmChangeImpact.HIGH,
    logoUrl: pinterestLogo,
    utmParams: [
      {
        key: "nbt",
        value: "nb:pinterest::{campaignid}:{adgroupid}:{adid}",
      },
      { key: "nb_platform", value: "pinterest" },

      { key: UtmParam.MEDIUM, value: "paid" },
      { key: UtmParam.SOURCE, value: "pinterest" },
      { key: UtmParam.CAMPAIGN, value: "{campaign_name}" },
      { key: UtmParam.TERM, value: "{adgroup_name}" },
    ],
  },
  [Platform.RAKUTEN]: {
    name: Platform.RAKUTEN,
    connectionType: ConnectionType.integration_rakuten,
    enableIntegrationFeatureFlagName: "enableRakutenConnection",
    utmChangeImpact: UtmChangeImpact.SOMEWHAT,
    skipUtmConfirmationStep: true,
    utmParams: [
      { key: "nb_platform", value: "rakuten" },
      { key: "nb_publisher", value: "[publisherID]" },
      { key: "nb_creative", value: "[creativeID]" },
      { key: "nb_offer", value: "[offerID]" },
    ],
  },
  [Platform.SNAPCHAT]: {
    name: Platform.SNAPCHAT,
    connectionType: ConnectionType.integration_snapchat,
    utmChangeImpact: UtmChangeImpact.HIGH,
    logoUrl: snapchatLogo,
    utmParams: [
      {
        key: "nbt",
        value:
          "nb%3Asnapchat%3A{{site_source_name}}%3A{{campaign.id}}%3A{{adSet.id}}%3A{{ad.id}}",
      },
    ],
  },

  [Platform.META]: {
    name: Platform.META,
    title: "Meta Ads",
    logoUrl: fbLogo,
    connectionType: ConnectionType.integration_facebook_ads,
    utmChangeImpact: UtmChangeImpact.HIGH,
    utmParams: [
      {
        key: "nbt",
        value:
          "nb:fb:{{site_source_name}}:{{campaign.id}}:{{adset.id}}:{{ad.id}}",
      },
      { key: "nb_placement", value: "{{placement}}" },
    ],
  },
  [Platform.MICROSOFT]: {
    name: Platform.MICROSOFT,
    logoUrl: microsoftLogo,
    connectionType: ConnectionType.integration_microsoft,
    utmParams: [],
    utmChangeImpact: UtmChangeImpact.NOT_IMPACTED,
  },
  [Platform.MNTN]: {
    name: Platform.MNTN,
    connectionType: ConnectionType.integration_mntn,
    enableIntegrationFeatureFlagName: "enableMntnConnection",
  },
  [Platform.TATARI]: {
    name: Platform.TATARI,
    connectionType: ConnectionType.integration_tatari,
    enableIntegrationFeatureFlagName: "enableTatariConnection",
  },
  [Platform.APPLOVIN]: {
    name: Platform.APPLOVIN,
    connectionType: ConnectionType.integration_applovin,
    enableIntegrationFeatureFlagName: "enableApplovinConnection",
    skipUtmConfirmationStep: true,
  },
  [Platform.TIKTOK]: {
    name: Platform.TIKTOK,
    connectionType: ConnectionType.integration_tiktok,
    utmChangeImpact: UtmChangeImpact.HIGH,
    logoUrl: tiktokLogo,
    utmParams: [
      { key: "nb_platform", value: "tiktok" },
      {
        key: "nbt",
        value: "nb:tiktok::__CAMPAIGN_ID__:__AID__:__CID__",
      },
    ],
  },
  [Platform.TWITTER]: {
    name: Platform.TWITTER,
    connectionType: ConnectionType.integration_twitter,
    utmChangeImpact: UtmChangeImpact.HIGH,
    utmParams: [
      { key: UtmParam.SOURCE, value: "twitter" },
      { key: UtmParam.MEDIUM, value: "paid" },
      { key: "nb_platform", value: "twitter" },
    ],
  },
};

export const getNorthbeamUtmPlatformConfig = (
  platform: NorthbeamUtmPlatform,
) => {
  return NORTHBEAM_PLATFORM_CONFIGS[platform];
};

export const usePlatformsMetadata = () => {
  const { loading: isLoadingMetadata, hasFeatureFlag } = useFeatureFlags();

  const platforms = reduce(
    NORTHBEAM_PLATFORM_CONFIGS,
    (parsedPlatforms, metadata, platform) => {
      const { enableIntegrationFeatureFlagName, ...remainingMetadata } =
        metadata;
      if (
        enableIntegrationFeatureFlagName &&
        !hasFeatureFlag(enableIntegrationFeatureFlagName)
      ) {
        remainingMetadata.connectionType = remainingMetadata.utmChangeImpact
          ? undefined
          : "manual";
        remainingMetadata.skipUtmConfirmationStep = false;
      }
      parsedPlatforms[platform as Platform] = remainingMetadata;
      return parsedPlatforms;
    },
    {} as PlatformsMetadataMapping,
  );

  const isConnectionSupported = (platform: Platform): boolean => {
    return !!platforms[platform].connectionType;
  };

  const isUTMSupported = (platform: Platform): boolean => {
    return !!platforms[platform].utmChangeImpact;
  };

  const isDisplayUTMConfirmationStep = (platform: Platform): boolean => {
    const skipUtmConfirmationStep =
      !!platforms[platform].skipUtmConfirmationStep;
    return isUTMSupported(platform) && !skipUtmConfirmationStep;
  };

  const platformsMissingConfiguration = (stepMetadata: StepMetadata) => {
    return Object.fromEntries(
      Object.entries(stepMetadata.platforms).filter(([platform, state]) => {
        return (
          (isConnectionSupported(platform as Platform) &&
            !state.isConnectionConfigured) ||
          (isUTMSupported(platform as Platform) && !state.isUTMsConfigured)
        );
      }),
    );
  };
  const getTitleForPlatform = (platform: Platform): string => {
    const metadata = platforms[platform];
    return metadata.title ? metadata.title : metadata.name;
  };

  function getMetadata(): PlatformMetadata[];
  function getMetadata(platform: Platform): PlatformMetadata;
  function getMetadata(
    platform?: Platform,
  ): PlatformMetadata | PlatformMetadata[] {
    if (platform !== undefined) {
      return platforms[platform];
    }

    return Object.values(platforms).filter(
      (platformMetadata) => platformMetadata.name !== Platform.UNKNOWN,
    );
  }

  return {
    platforms,
    isLoadingMetadata,
    isConnectionSupported,
    isUTMSupported,
    isDisplayUTMConfirmationStep,
    platformsMissingConfiguration,
    getTitleForPlatform,
    getMetadata,
  };
};

export const PlatformMetadataStore = {
  all: (): PlatformMetadata[] =>
    Object.values(NORTHBEAM_PLATFORM_CONFIGS).filter(
      (platform) => platform.name !== Platform.UNKNOWN,
    ),
  get: (platform: Platform): PlatformMetadata =>
    NORTHBEAM_PLATFORM_CONFIGS[platform],
};

export function getPlatformTitle(platform: PlatformMetadata): string {
  return platform.title ? platform.title : platform.name;
}

export function getTitleForPlatform(platform: Platform): string {
  const metadata = PlatformMetadataStore.get(platform);
  return metadata.title ? metadata.title : metadata.name;
}

export const sortPlatformMetadata = (
  a: PlatformMetadata,
  b: PlatformMetadata,
): number => getPlatformTitle(a).localeCompare(getPlatformTitle(b));

export function getNorthbeamUtmPlatformParams(
  platform?: Platform,
): UtmPlatformParam[] {
  const params =
    platform !== undefined
      ? NORTHBEAM_PLATFORM_CONFIGS[platform].utmParams
      : Object.values(NORTHBEAM_PLATFORM_CONFIGS).flatMap(
          (config) => config.utmParams,
        );
  if (!params) {
    return [];
  }
  return params
    .filter((param) => !!param)
    .map((param): UtmPlatformParam => {
      if (!param) {
        return { key: "nbt", value: "" };
      }
      const { key, value } = param;
      return typeof value === "function"
        ? { key, value: value() }
        : { key, value };
    });
}
